/**
 * Converts a prose string (paragraph) into an array of bullet-point strings.
 *
 * Splits on sentence-ending punctuation (. ! ?) followed by whitespace,
 * while avoiding false splits on common abbreviations, decimal numbers,
 * ellipses, and URLs.
 *
 * @param {string} text - The prose text to split into bullet points.
 * @returns {string[]} An array of trimmed, non-empty sentences.
 *
 * @example
 *   proseToBullets("Do not click any links. Report the email as spam. Contact your bank immediately.")
 *   // => ["Do not click any links.", "Report the email as spam.", "Contact your bank immediately."]
 */
export function proseToBullets(text) {
  if (!text || typeof text !== "string") return [];

  // Check if the text contains markdown-style bullet lines (- item or * item or 1. item)
  const markdownBulletPattern = /^[\s]*[-*•]\s+.+/m;
  const numberedListPattern = /^[\s]*\d+\.\s+.+/m;
  if (markdownBulletPattern.test(text) || numberedListPattern.test(text)) {
    // Split by newlines and extract bullet items
    return text
      .split(/\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => line.replace(/^[-*•]\s+/, "").replace(/^\d+\.\s+/, ""))
      .map((line) => line.replace(/\*\*(.+?)\*\*/g, "$1")) // strip markdown bold
      .filter((line) => line.length > 0);
  }

  // Strip any HTML tags to get plain text
  const plain = text
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return [];

  // Regex splits on . ! or ? that are followed by a space and an uppercase letter,
  // or at end-of-string — but not when preceded by common abbreviations or digits.
  //
  // Strategy: walk through the string and split at sentence boundaries.
  const abbreviations = new Set([
    "mr",
    "mrs",
    "ms",
    "dr",
    "prof",
    "sr",
    "jr",
    "vs",
    "etc",
    "inc",
    "ltd",
    "dept",
    "est",
    "approx",
    "e.g",
    "i.e",
    "apt",
    "no",
    "vol",
    "st",
    "ave",
    "blvd",
    "ft",
  ]);

  const sentences = [];
  let current = "";

  // Tokenize by splitting on sentence-ending punctuation followed by space
  // Use a regex that captures the delimiter
  const parts = plain.split(/([.!?]+[\s]+)/);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    // Check if this part is a delimiter (punctuation + space)
    if (/^[.!?]+[\s]+$/.test(part)) {
      current += part.trimEnd();

      // Check if this is a false split (abbreviation or number before the period)
      const beforePunctuation = current.trimEnd();
      const lastWord = beforePunctuation
        .split(/\s+/)
        .pop()
        .replace(/[.!?]+$/, "")
        .toLowerCase();
      const endsWithDigit = /\d\.$/.test(beforePunctuation);

      if (abbreviations.has(lastWord) || endsWithDigit) {
        // False split — keep accumulating
        current += " ";
        continue;
      }

      // Valid sentence boundary
      const trimmed = current.trim();
      if (trimmed) sentences.push(trimmed);
      current = "";
    } else {
      current += part;
    }
  }

  // Push any remaining text
  const remaining = current.trim();
  if (remaining) sentences.push(remaining);

  // If we ended up with only one "sentence" that equals the original,
  // try a more aggressive split on any period/exclamation/question mark
  // followed by a space (regardless of case)
  if (sentences.length <= 1 && plain.length > 0) {
    const aggressive = plain
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (aggressive.length > 1) return aggressive;
  }

  return sentences;
}

export default proseToBullets;
